import type { TokenAllowanceData } from '@revoke.cash/core/allowances';
import { chainIdSorter } from '@revoke.cash/core/chains';
import { isApprovalTokenEvent, type TokenEvent } from '@revoke.cash/core/events';
import ky from '@revoke.cash/core/ky';
import type { AddressOnChain } from '@revoke.cash/core/types';
import { deduplicateArray } from '@revoke.cash/core/utils';
import type { Address } from 'viem';

export type Exploit = {
  slug: string;
  name: string;
  description: string;
  longDescription?: string;
  date: string;
  amount: number;
  fixed?: boolean;
  addresses: AddressOnChain[];
  metaArticleUrls: string[];
};

export type ExploiStatus = 'affected' | 'safe' | 'previously_affected';

const ORGANISATION = 'RevokeCash';
const REPOSITORY = 'approval-exploit-list';
export const REPORT_EXPLOIT_URL = `https://github.com/${ORGANISATION}/${REPOSITORY}/issues/new?assignees=&labels=submission%2Cexploit&projects=&template=REPORT-EXPLOIT.yml`;

export const isValidExploitSlug = (slug: string): boolean => /^[a-z0-9-]+$/.test(slug) && slug.length > 0;

export const fetchExploitSlugs = async (): Promise<string[]> => {
  return (await fetchGithubFile(ORGANISATION, REPOSITORY, '/index.json')) as string[];
};

export const fetchExploit = async (slug: string): Promise<Exploit> => {
  if (!isValidExploitSlug(slug)) throw new Error(`Invalid exploit slug '${slug}'`);

  const exploit = (await fetchGithubFile(ORGANISATION, REPOSITORY, `/exploits/${slug}.json`)) as Exploit;
  exploit.slug = slug;

  return exploit;
};

const fetchGithubFile = async (organisation: string, repository: string, path: string) => {
  const url = `https://raw.githubusercontent.com/${organisation}/${repository}/main${path}`;
  const text = await ky.get(url, { timeout: 10_000 }).text();

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

export const getGlobalExploitStats = (exploits: Exploit[]) => {
  const totalAmount = formatExploitAmount(Math.floor(exploits.reduce((prev, curr) => prev + curr.amount, 0)));
  const earliestYear = exploits.at(-1)!.date.slice(0, 4);

  return { totalAmount, earliestYear };
};

export const getUniqueChainIds = (exploit: Exploit): number[] => {
  const allChainIds = exploit.addresses.map((address) => address.chainId);
  return deduplicateArray(allChainIds).sort(chainIdSorter);
};

export interface ExploitStatusResult {
  status: ExploiStatus;
  matchedSpenders: AddressOnChain[];
}

export const getExploitStatus = (
  events: TokenEvent[],
  allowances: TokenAllowanceData[],
  exploit: Exploit,
): ExploitStatusResult => {
  const affectedSpenders = getMatchedSpenders(allowances, exploit);
  if (affectedSpenders.length > 0) {
    return { status: 'affected', matchedSpenders: affectedSpenders };
  }

  const previouslyAffectedSpenders = getMatchedSpenders(events.filter(isApprovalTokenEvent), exploit);
  if (previouslyAffectedSpenders.length > 0) {
    return { status: 'previously_affected', matchedSpenders: previouslyAffectedSpenders };
  }

  return { status: 'safe', matchedSpenders: [] };
};

interface EventOrAllowance {
  payload: {
    spender: Address;
  };
  chainId: number;
}

const getMatchedSpenders = (eventsOrAllowances: Array<EventOrAllowance>, exploit: Exploit): AddressOnChain[] => {
  return exploit.addresses.filter((exploitAddress) =>
    eventsOrAllowances.some(
      (eventOrAllowance) =>
        eventOrAllowance.payload.spender === exploitAddress.address &&
        eventOrAllowance.chainId === exploitAddress.chainId,
    ),
  );
};

export interface MultichainExploitResult {
  exploit: Exploit;
  affectedSpenders: AddressOnChain[];
  previouslyAffectedSpenders: AddressOnChain[];
  status: ExploiStatus;
}

export const getMultichainExploitResults = (
  eventsByChain: Map<number, TokenEvent[]>,
  allowancesByChain: Map<number, TokenAllowanceData[]>,
  exploits: Exploit[],
): MultichainExploitResult[] => {
  return exploits.map((exploit) => {
    const exploitChainIds = getUniqueChainIds(exploit);

    const affectedSpenders = exploitChainIds.flatMap((chainId) => {
      const allowances = allowancesByChain.get(chainId) ?? [];
      return getMatchedSpenders(allowances, exploit);
    });

    const previouslyAffectedSpenders = exploitChainIds
      .filter((chainId) => !affectedSpenders.some((spender) => spender.chainId === chainId))
      .flatMap((chainId) => {
        const events = (eventsByChain.get(chainId) ?? []).filter(isApprovalTokenEvent);
        return getMatchedSpenders(events, exploit);
      });

    const status =
      affectedSpenders.length > 0 ? 'affected' : previouslyAffectedSpenders.length > 0 ? 'previously_affected' : 'safe';

    return { exploit, affectedSpenders, previouslyAffectedSpenders, status };
  });
};

export const formatExploitAmount = (amountInMillions: number | null) => {
  if (amountInMillions === null) return 'Unknown';
  if (amountInMillions === 0) return `$${amountInMillions}`;
  if (amountInMillions < 1) return `$${amountInMillions * 1000}k`;
  return `$${amountInMillions}M`;
};
